// ============================================================================
// ARBITRATOR SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { arbitratorEndpoints, ARBITRATOR_BASE_PATH } from '../endpoints/arbitrator.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Arbitrator,
  ArbitratorListItem,
  CreateArbitratorRequest,
  UpdateArbitratorRequest,
  ArbitratorSearchRequest,
} from '../types/arbitrator.types';

class ArbitratorService {
  async getAll(params?: ArbitratorSearchRequest): Promise<PaginatedResponse<ArbitratorListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ARBITRATOR_BASE_PATH, params)
      : arbitratorEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Arbitrator>> {
    const response = await get<ApiResponse<Arbitrator>>(arbitratorEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Arbitrator>> {
    const response = await get<ApiResponse<Arbitrator>>(arbitratorEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateArbitratorRequest): Promise<ApiResponse<Arbitrator>> {
    const response = await post<ApiResponse<Arbitrator>>(arbitratorEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateArbitratorRequest): Promise<ApiResponse<Arbitrator>> {
    const response = await put<ApiResponse<Arbitrator>>(arbitratorEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(arbitratorEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${arbitratorEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : arbitratorEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const arbitratorService = new ArbitratorService();
export default arbitratorService;
